import { formatDateTimeFull } from '@/shared/formatters';
import { TIPOS_ENCERRAMENTO_COM_ACORDO } from '../constants/tiposEncerramento';
import type { TipoEncerramento } from '../types/encerramentoTypes';
import type { NegociacaoCasoListItem, NegociacaoCasoStatus } from '../types/negociacoesTypes';
import type {
  BadgeVariant,
  NegociacaoListRow,
  NegociacaoTemperature,
} from '../types/negociacoesUiTypes';
import { resolveObjectiveLabel, resolveObjectiveValue } from './negociacoesFilterOptions';

export const statusLabelMap: Record<NegociacaoCasoStatus, string> = {
  SCHEDULED: 'Aguardando envio',
  NEGOTIATION_STARTED: 'Aguardando resposta',
  ACTIVE: 'Em negociação',
  AGREEMENT_CLOSED: 'Acordo fechado',
  NO_AGREEMENT: 'Proposta recusada',
  PAUSED: 'Pausada',
  ARCHIVED: 'Arquivada',
  FAILED: 'Falha no envio',
  CLOSED: 'Interrompida',
};

export const statusVariantMap: Record<NegociacaoCasoStatus, BadgeVariant> = {
  SCHEDULED: 'warning',
  NEGOTIATION_STARTED: 'brand',
  ACTIVE: 'brand',
  AGREEMENT_CLOSED: 'success',
  NO_AGREEMENT: 'error',
  PAUSED: 'neutral',
  ARCHIVED: 'neutral',
  FAILED: 'error',
  CLOSED: 'warning',
};

export const statusPillClassMap: Record<NegociacaoCasoStatus, string> = {
  SCHEDULED: 'neg-status-pill--scheduled',
  NEGOTIATION_STARTED: 'neg-status-pill--negotiation_started',
  ACTIVE: 'neg-status-pill--active',
  PAUSED: 'neg-status-pill--paused',
  AGREEMENT_CLOSED: 'neg-status-pill--agreement_closed',
  NO_AGREEMENT: 'neg-status-pill--no_agreement',
  CLOSED: 'neg-status-pill--closed',
  FAILED: 'neg-status-pill--failed',
  ARCHIVED: 'neg-status-pill--archived',
};

export const closedStatusLabelMap: Record<TipoEncerramento, string> = {
  ACORDO_MANUAL: 'Acordo fechado',
  ACORDO_IA: 'Acordo fechado (IA)',
  DESISTENCIA: 'Encerrada por desistência',
  TIMEOUT: 'Encerrada por timeout',
  CANCELAMENTO: 'Encerrada por cancelamento',
};

export const closedStatusVariantMap: Record<TipoEncerramento, BadgeVariant> = {
  ACORDO_MANUAL: 'success',
  ACORDO_IA: 'success',
  DESISTENCIA: 'error',
  TIMEOUT: 'warning',
  CANCELAMENTO: 'warning',
};

export const cadenceToTemperature = (cadenceBucket: string): NegociacaoTemperature => {
  if (cadenceBucket === 'HOT') return 'Quente';
  if (cadenceBucket === 'WARM') return 'Morna';
  if (cadenceBucket === 'COLD') return 'Fria';
  return 'Morna';
};

export const toNegociacaoListRow = (item: NegociacaoCasoListItem): NegociacaoListRow => {
  const isArchived = Boolean(item.archivedAt);
  return {
    id: item.id,
    client: item.counterpartyName,
    negotiationName: item.titulo,
    numeroNegociacao: item.numeroNegociacao,
    createdAt: formatDateTimeFull(item.dataCriacao),
    ultimaInteracaoEm: formatDateTimeFull(item.ultimaInteracaoEm),
    dataAcordo: TIPOS_ENCERRAMENTO_COM_ACORDO.has(item.tipoEncerramento ?? '')
      ? formatDateTimeFull(item.dataEncerramento)
      : '',
    type: item.isBulk ? 'Lote' : 'Individual',
    objective: resolveObjectiveLabel(item.scenarioType),
    objectiveValue: resolveObjectiveValue(item.scenarioType),
    temperature: cadenceToTemperature(item.cadenceBucket),
    status: isArchived ? 'Arquivada' : statusLabelMap[item.status],
    statusValue: item.status,
    statusVariant: isArchived ? 'neutral' : statusVariantMap[item.status],
    situacoes: item.situacoes.map((s) => ({ label: s.label, key: s.key })),
    pendency: item.pendencyTitle,
    needsAction: item.atendimentoHumanoAtivo,
    isPaused: item.status === 'PAUSED',
    isArchived,
    tipoEncerramento: item.tipoEncerramento,
    customFieldValues: item.customFieldValues,
  };
};
